import { useState } from 'react';

import { EntitySelect } from '@/components/EntitySelect';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { ApiError } from '@/core/api';
import { resolveProjectLabel, searchProjects } from '@/features/projects/select';
import { cn } from '@/lib/utils';
import { useAcceptProposal, useAcceptProposalOnly } from '../queries';
import type { Proposal } from '../types';

type Mode = 'new' | 'existing' | 'none';

const MODES: { value: Mode; label: string }[] = [
  { value: 'new', label: 'New project' },
  { value: 'existing', label: 'Existing project' },
  { value: 'none', label: 'No project' },
];

/**
 * Accepting a proposal walks the spine (contract → project). Three shapes:
 *
 * - **new** — provision a project; the name defaults to the proposal title but
 *   is editable, since proposal titles are often far too long for a project.
 * - **existing** — accept without provisioning, then link the project the work
 *   already has onto the drafted contract (signing later reuses it).
 * - **none** — accept only: proposal ACCEPTED, deal WON, DRAFT contract, no
 *   project.
 */
export function AcceptProposalDialog({
  open,
  onOpenChange,
  proposal,
  onAcceptedWithProject,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  proposal: Proposal;
  /** Navigate to the project after a `new`/`existing` accept. */
  onAcceptedWithProject: (projectId: number) => void;
}) {
  const accept = useAcceptProposal();
  const acceptOnly = useAcceptProposalOnly();

  const [mode, setMode] = useState<Mode>('new');
  const [name, setName] = useState(proposal.title ?? '');
  const [projectId, setProjectId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const busy = accept.isPending || acceptOnly.isPending;
  const canSubmit =
    mode === 'new' ? name.trim() !== '' : mode === 'existing' ? projectId !== '' : true;

  async function handleSubmit() {
    setError(null);
    try {
      if (mode === 'new') {
        const project = await accept.mutateAsync({
          id: proposal.id,
          project_name: name.trim(),
        });
        onOpenChange(false);
        onAcceptedWithProject(project.id);
        return;
      }

      // Both remaining modes accept without provisioning. `existing` passes the
      // project so the server links it onto the drafted contract atomically —
      // no half-state where the proposal is accepted but nothing is linked.
      await acceptOnly.mutateAsync({
        id: proposal.id,
        project: mode === 'existing' ? Number(projectId) : undefined,
      });
      onOpenChange(false);
      if (mode === 'existing') onAcceptedWithProject(Number(projectId));
    } catch (e) {
      const err = e as ApiError;
      setError(
        err?.fieldErrors ? Object.values(err.fieldErrors).flat().join(' ') : err?.message,
      );
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Accept proposal</DialogTitle>
          <DialogDescription>
            Accepting wins the deal and drafts a contract. Choose what happens to
            delivery.
          </DialogDescription>
        </DialogHeader>

        <div
          role="group"
          aria-label="Project handling"
          className="border-input bg-background inline-flex w-fit overflow-hidden rounded-md border"
        >
          {MODES.map(({ value, label }) => (
            <button
              key={value}
              type="button"
              onClick={() => setMode(value)}
              aria-pressed={mode === value}
              className={cn(
                'inline-flex h-9 items-center px-3 text-sm transition-colors',
                mode === value
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-foreground',
              )}
            >
              {label}
            </button>
          ))}
        </div>

        {mode === 'new' ? (
          <div className="space-y-1.5">
            <Label htmlFor="accept-project-name">Project name</Label>
            <Input
              id="accept-project-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Short name for the project"
            />
            <p className="text-muted-foreground text-xs">
              Defaults to the proposal title — shorten it if you like.
            </p>
          </div>
        ) : null}

        {mode === 'existing' ? (
          <div className="space-y-1.5">
            <Label>Project</Label>
            <EntitySelect
              value={projectId}
              onChange={setProjectId}
              search={searchProjects}
              resolveLabel={resolveProjectLabel}
              placeholder="Search projects…"
              queryKey="accept-proposal-project"
            />
            <p className="text-muted-foreground text-xs">
              Linked to the drafted contract; no new project is created.
            </p>
          </div>
        ) : null}

        {mode === 'none' ? (
          <p className="text-muted-foreground text-sm">
            The proposal is accepted and the deal won. A draft contract is created
            without a project — you can link one later from the contract.
          </p>
        ) : null}

        {error ? <p className="text-destructive text-sm">{error}</p> : null}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit || busy}>
            {busy ? 'Accepting…' : 'Accept'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
